/**
 * CENTRALIZED API SERVICE LAYER
 * 
 * This service abstracts all API communications for the Block Management system.
 * It handles both Firebase Functions (httpsCallable) and REST endpoints with
 * automatic authentication, error handling, and loading states.
 * 
 * CRITICAL: This ensures consistent API behavior across the entire application
 * and eliminates repeated auth/error handling code.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { app } from '../firebase'; // Import the Firebase app instance

// Firebase Functions instance - connect to authenticated app
const functions = getFunctions(app);

// API Configuration
const API_CONFIG = {
  baseUrl: 'https://us-central1-blockautomation-ac669.cloudfunctions.net',
  timeout: 30000, // 30 seconds for device operations
  retryAttempts: 3,
  retryDelay: 1000 // 1 second base delay
};

// API Error Types
export const API_ERROR_TYPES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  DEVICE_OFFLINE: 'DEVICE_OFFLINE',
  DEVICE_TIMEOUT: 'DEVICE_TIMEOUT',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Custom API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(message, type = API_ERROR_TYPES.UNKNOWN_ERROR, originalError = null) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Core API Service Class
 */
class ApiService {
  constructor() {
    this.auth = getAuth();
    this.loadingStates = new Map();
    this.requestCache = new Map();
  }

  /**
   * Get authenticated user token
   */
  async getAuthToken() {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new ApiError('User not authenticated', API_ERROR_TYPES.UNAUTHORIZED);
      }
      return await user.getIdToken(true); // Force refresh
    } catch (error) {
      throw new ApiError('Failed to get auth token', API_ERROR_TYPES.UNAUTHORIZED, error);
    }
  }

  /**
   * Get authenticated headers for REST calls
   */
  async getAuthHeaders() {
    const token = await this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Set loading state for a specific operation
   */
  setLoading(key, isLoading) {
    this.loadingStates.set(key, isLoading);
  }

  /**
   * Check if operation is loading
   */
  isLoading(key) {
    return this.loadingStates.get(key) || false;
  }

  /**
   * Create loading key for operation
   */
  createLoadingKey(operation, identifier = '') {
    return `${operation}${identifier ? `_${identifier}` : ''}`;
  }

  /**
   * Handle API errors with user-friendly messages
   */
  handleApiError(error, operation, identifier = '') {
    console.error(`API Error [${operation}${identifier ? ` - ${identifier}` : ''}]:`, error);
    
    // Additional debugging for [object Object] errors
    if (error && typeof error === 'object') {
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        data: error.data,
        response: error.response
      });
    }

    // Backend structured error responses (from our new handleError function)
    if (error.message && error.message.includes('error')) {
      try {
        const errorData = JSON.parse(error.message);
        if (errorData.error && errorData.error.code) {
          switch (errorData.error.code) {
            case 'DEVICE_OFFLINE':
              return new ApiError(
                `Device ${identifier} is offline. Please check device connection and try again.`,
                API_ERROR_TYPES.DEVICE_OFFLINE,
                error
              );
            case 'DEVICE_TIMEOUT':
              return new ApiError(
                `Device ${identifier} response timed out. Please try again.`,
                API_ERROR_TYPES.DEVICE_TIMEOUT,
                error
              );
            default:
              return new ApiError(
                errorData.error.message || `Device operation failed: ${error.message}`,
                API_ERROR_TYPES.SERVER_ERROR,
                error
              );
          }
        }
      } catch (parseError) {
        // If JSON parsing fails, continue with regular error handling
      }
    }

    // Firebase Function errors
    if (error.code) {
      switch (error.code) {
        case 'functions/unavailable':
          return new ApiError(
            `Device ${identifier} is offline. Operation will be processed when device reconnects.`,
            API_ERROR_TYPES.DEVICE_OFFLINE,
            error
          );
        case 'functions/deadline-exceeded':
          return new ApiError(
            `Device ${identifier} response timed out. Please try again.`,
            API_ERROR_TYPES.DEVICE_TIMEOUT,
            error
          );
        case 'functions/unauthenticated':
          return new ApiError(
            'Authentication required. Please log in again.',
            API_ERROR_TYPES.UNAUTHORIZED,
            error
          );
        case 'functions/permission-denied':
          return new ApiError(
            'Permission denied. You may not have access to this operation.',
            API_ERROR_TYPES.UNAUTHORIZED,
            error
          );
        case 'functions/invalid-argument':
          return new ApiError(
            'Invalid request parameters. Please check your input.',
            API_ERROR_TYPES.VALIDATION_ERROR,
            error
          );
        default:
          return new ApiError(
            `Device operation failed: ${error.message}`,
            API_ERROR_TYPES.SERVER_ERROR,
            error
          );
      }
    }

    // HTTP errors
    if (error.response || error.status) {
      const status = error.response?.status || error.status;
      switch (status) {
        case 401:
          return new ApiError(
            'Authentication required. Please log in again.',
            API_ERROR_TYPES.UNAUTHORIZED,
            error
          );
        case 403:
          return new ApiError(
            'Permission denied. You may not have access to this operation.',
            API_ERROR_TYPES.UNAUTHORIZED,
            error
          );
        case 404:
          return new ApiError(
            'Resource not found. The requested item may have been deleted.',
            API_ERROR_TYPES.VALIDATION_ERROR,
            error
          );
        case 500:
          return new ApiError(
            'Server error. Please try again later.',
            API_ERROR_TYPES.SERVER_ERROR,
            error
          );
        default:
          return new ApiError(
            `Request failed with status ${status}`,
            API_ERROR_TYPES.SERVER_ERROR,
            error
          );
      }
    }

    // Network errors
    if (error.message?.includes('NetworkError') || error.message?.includes('fetch')) {
      return new ApiError(
        'Network error. Please check your internet connection.',
        API_ERROR_TYPES.NETWORK_ERROR,
        error
      );
    }

    // Default error
    return new ApiError(
      error.message || 'An unexpected error occurred',
      API_ERROR_TYPES.UNKNOWN_ERROR,
      error
    );
  }

  /**
   * Make a Firebase Function call with automatic error handling
   */
  async callFunction(functionName, data = {}, options = {}) {
    const loadingKey = this.createLoadingKey(functionName, options.identifier);
    
    try {
      this.setLoading(loadingKey, true);
      
      const callable = httpsCallable(functions, functionName);
      const result = await callable(data);
      
      // Validate response
      if (!result.data) {
        throw new Error('No data returned from function');
      }
      
      if (result.data.success === false) {
        // Better error handling for structured backend errors
        const errorMessage = result.data.message || result.data.error;
        const errorDetails = {
          message: errorMessage,
          code: result.data.code,
          data: result.data,
          functionName,
          requestData: data
        };
        
        console.error('Backend function returned error:', errorDetails);
        
        // Create a more informative error
        const structuredError = new Error(errorMessage || 'Function returned failure');
        structuredError.code = result.data.code;
        structuredError.data = result.data;
        throw structuredError;
      }
      
      return result.data;
      
    } catch (error) {
      // Enhanced error logging
      console.error(`Firebase Function [${functionName}] error:`, {
        error: error.message,
        code: error.code,
        data: error.data,
        requestData: data,
        identifier: options.identifier
      });
      
      throw this.handleApiError(error, functionName, options.identifier);
    } finally {
      this.setLoading(loadingKey, false);
    }
  }

  /**
   * Make a REST API call with automatic auth and error handling
   */
  async callRestApi(endpoint, data = null, method = 'GET', options = {}) {
    const loadingKey = this.createLoadingKey(endpoint, options.identifier);
    
    try {
      this.setLoading(loadingKey, true);
      
      const headers = await this.getAuthHeaders();
      const url = `${API_CONFIG.baseUrl}/${endpoint}`;
      
      const requestConfig = {
        method,
        headers,
        ...(data && { body: JSON.stringify(data) })
      };
      
      const response = await fetch(url, requestConfig);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.response = { status: response.status };
        throw error;
      }
      
      return await response.json();
      
    } catch (error) {
      throw this.handleApiError(error, endpoint, options.identifier);
    } finally {
      this.setLoading(loadingKey, false);
    }
  }

  /**
   * Retry mechanism for critical operations
   */
  async withRetry(operation, maxRetries = API_CONFIG.retryAttempts) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        // Don't retry authentication or validation errors
        if (error.type === API_ERROR_TYPES.UNAUTHORIZED || 
            error.type === API_ERROR_TYPES.VALIDATION_ERROR) {
          throw error;
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => 
            setTimeout(resolve, API_CONFIG.retryDelay * Math.pow(2, attempt - 1))
          );
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Get all blocks with comprehensive stats and status information
   * @returns {Promise<Array>} Array of block objects with full data
   */
  async getBlocksWithStats() {
    const loadingKey = this.createLoadingKey('getBlocksWithStats');
    this.setLoading(loadingKey, true);
    
    try {
      const result = await this.callFunction('getBlocksWithStats', {});
      
      if (result.success) {
        return result.blocks || [];
      } else {
        throw new ApiError(
          result.error || 'Failed to fetch blocks with stats',
          API_ERROR_TYPES.SERVER_ERROR
        );
      }
    } catch (error) {
      throw this.handleApiError(error, 'getBlocksWithStats');
    } finally {
      this.setLoading(loadingKey, false);
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

/**
 * DEVICE CONTROL API METHODS
 * These handle the 13 Firebase Functions for device control
 */
export const deviceApi = {
  async paymentConfirmed(serial, paymentDataOrDispenseCount) {
    // Support both formats:
    // 1. New format: paymentConfirmed(serial, paymentData) where paymentData is "payment_id|dispenses_granted|price_paid|sunscreen_name"
    // 2. Legacy format: paymentConfirmed(serial, dispenseCount) for backward compatibility
    
    let paymentData;
    
    if (typeof paymentDataOrDispenseCount === 'string' && paymentDataOrDispenseCount.includes('|')) {
      // New format: already formatted payment data string
      paymentData = paymentDataOrDispenseCount;
    } else {
      // Legacy format: just dispense count, generate default payment data
      const dispenseCount = paymentDataOrDispenseCount || 1;
      const paymentId = `pi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const price = '0.00'; // Default price for legacy calls
      const sunscreenName = 'sunscreen'; // Default sunscreen name
      paymentData = `${paymentId}|${dispenseCount}|${price}|${sunscreenName}`;
    }
    
    // Use super-handler for payment confirmation
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'paymentConfirmed', 
        serial, 
        paymentData 
      }, 
      { identifier: serial }
    );
  },

  async setMode(serial, mode) {
    // Use super-handler for device commands
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'setMode', 
        serial, 
        mode: mode.toUpperCase() 
      }, 
      { identifier: serial }
    );
  },

  async sensorControl(serial, operation, parameters = {}) {
    // Use super-handler for sensor operations
    if (operation === 'calibrateProximity') {
      const command = parameters.type || 'baseline';
      return await apiService.callFunction('deviceCommands', 
        { 
          command: 'calibrateProximity', 
          serial, 
          calibrationCommand: command 
        }, 
        { identifier: serial }
      );
    }
    
    // For other sensor operations, use device commands super-handler
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'controlSensors', 
        serial, 
        operation, 
        ...parameters 
      }, 
      { identifier: serial }
    );
  },

  async weatherControl(serial, action, parameters = {}) {
    // Use super-handler for weather control - send separate action and data parameters
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'weatherControl', 
        serial, 
        action: action.toUpperCase(),
        data: parameters.value || null
      }, 
      { identifier: serial }
    );
  },

  async motorControl(serial, motorCommand, value = null) {
    // Use super-handler for motor control commands
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'motorControl', 
        serial, 
        motorCommand,
        value
      }, 
      { identifier: serial }
    );
  },

  // Legacy UV control wrapper for backward compatibility
  async uvControl(serial, operation, parameters = {}) {
    console.warn('uvControl is deprecated. Use weatherControl instead.');
    
    // Map legacy UV operations to weather control via super-handler
    const uvIndex = parameters.uvIndex || 7.0;
    const actionData = `UV|${uvIndex}`;
    
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'controlUV', 
        serial, 
        action: actionData 
      }, 
      { identifier: serial }
    );
  },

  async resetDevice(serial) {
    // Use super-handler for device reset
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'resetDevice', 
        serial, 
        resetType: 'DEVICE' 
      }, 
      { identifier: serial }
    );
  },

  async getDeviceStatus(serial) {
    // Call backend function that implements the function + variable pattern securely
    // Backend will:
    // 1. POST to trigger deviceStatus function update
    // 2. GET to read the updated deviceStatus variable
    // 3. Parse and return the JSON data
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'getDeviceStatus', 
        serial,
        statusType: 'status' // Request full device status including slots, mode, etc.
      }, 
      { identifier: serial }
    );
  },

  async clearErrors(serial, errorType = 'ALL') {
    // Use super-handler for clearing errors
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'clearErrors', 
        serial, 
        errorType: errorType.toUpperCase() 
      }, 
      { identifier: serial }
    );
  },

  async registerRefill(serial, refillData) {
    // Use super-handler with new parameter structure
    const { 
      volumeML, 
      fillLevel, // Legacy support
      sunscreenType = 'SPF30', 
      slotIndex = 0 
    } = refillData;

    // Handle 'FULL' refill command
    let actualVolumeML;
    if (volumeML === 'FULL' || fillLevel === 'FULL') {
      actualVolumeML = 5000; // Full capacity
    } else {
      actualVolumeML = volumeML !== null ? volumeML : fillLevel;
    }
  
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'registerRefill', 
        serial, 
        volumeML: actualVolumeML,
        fillLevel, // For backward compatibility
        sunscreenType,
        slotIndex
      }, 
      { identifier: serial }
    );
  },

  async runDiagnostics(serial, diagnosticType = 'all') {
    // Use super-handler for diagnostics
    const type = diagnosticType.toUpperCase();
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'runDiagnostics', 
        serial, 
        diagnosticType: type 
      }, 
      { identifier: serial }
    );
  },

  async setTimeWindow(serial, timeWindow) {
    // Use super-handler for time window settings
    const { startHour, endHour, days } = timeWindow;
    const hoursPayload = days ? `${days}:${startHour},${endHour}` : `${startHour},${endHour}`;
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'setTimeWindow', 
        serial, 
        timeWindow: hoursPayload 
      }, 
      { identifier: serial }
    );
  },

  async getDeviceLog(serial, logType = 'recent') {
    // Use super-handler for device log retrieval
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'getDeviceLog', 
        serial, 
        logType 
      }, 
      { identifier: serial }
    );
  },

  async manageEventBudget(serial, command, value = null) {
    // Use super-handler for event budget management
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'eventBudget', 
        serial, 
        budgetCommand: command, 
        value 
      }, 
      { identifier: serial }
    );
  },

  async requestStatistics(serial, statsType = 'week') {
    // Use super-handler for statistics request
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'requestStatistics', 
        serial, 
        statsType 
      }, 
      { identifier: serial }
    );
  },

  // NEW: Slot Management functions for CloudManager.cpp compatibility
  async manageSlots(serial, command, parameters = {}) {
    // Use super-handler for slot management operations
    return await apiService.callFunction('deviceCommands', 
      { 
        command: 'slotManagement', 
        serial, 
        slotCommand: command.toUpperCase(),
        ...parameters 
      }, 
      { identifier: serial }
    );
  },

  async configureSlots(serial, slotCount, selectionMode, defaultSlot) {
    // Configure slots using the format: "SLOT_COUNT,SELECTION_MODE,DEFAULT_SLOT"
    const configData = `${slotCount},${selectionMode},${defaultSlot}`;
    return await this.manageSlots(serial, 'SET', { configData });
  },

  async enableSlot(serial, slotIndex) {
    // Enable specific slot
    return await this.manageSlots(serial, 'ENABLE', { slotIndex });
  },

  async disableSlot(serial, slotIndex) {
    // Disable specific slot
    return await this.manageSlots(serial, 'DISABLE', { slotIndex });
  },

  async getSlotStatus(serial) {
    // Get slot status
    return await this.manageSlots(serial, 'STATUS');
  }
};

/**
 * BLOCK MANAGEMENT API METHODS
 * These handle REST endpoints for block management operations
 */
export const blockApi = {
  async createBlock(serial, name = null) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'createBlock', serial, name }, 
      { identifier: serial }
    );
  },

  async updateBlockStatus(serial, status) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'updateBlockStatus', serial, status }, 
      { identifier: serial }
    );
  },

  async updateBlockLocation(serial, location) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'updateBlockLocation', serial, location }, 
      { identifier: serial }
    );
  },

  async updateBlockAddress(serial, address) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'updateBlockAddress', serial, address }, 
      { identifier: serial }
    );
  },

  async getBlocksWithStats() {
    // Use the direct Firebase callable function instead of REST API
    return await apiService.callFunction('getBlocksWithStats', {});
  },

  async getBlockHealth(serial) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'getBlockHealth', serial }, 
      { identifier: serial }
    );
  },

  async getBlockStatus(serial) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'getBlockStatus', serial }, 
      { identifier: serial }
    );
  },

  async getBlockInfo(serial) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'getBlockInfo', serial }, 
      { identifier: serial }
    );
  },

  async logMaintenance(serial, maintenanceType, notes = '') {
    return await apiService.callFunction('blockOperations', {
      operation: 'logMaintenance',
      serial,
      maintenanceType,
      notes
    }, { identifier: serial });
  },

  async updateBlockImage(serial, imageData, imageType = 'profile') {
    console.log('🔧 API Service - updateBlockImage called:', {
      serial,
      imageType,
      imageDataLength: imageData?.length,
      imageDataPrefix: imageData?.substring(0, 50) + '...'
    });
  
    const payload = {
      operation: 'updateBlockImage',
      serial,
      imageData: imageData, // Fixed: backend expects imageData, not imageUrl
      imageType
    };
  
    console.log('📤 Calling Firebase function with payload:', payload);
  
    try {
      const result = await apiService.callFunction('blockOperationsCompat', payload);
      console.log('📥 Firebase function response:', result);
      return result;
    } catch (error) {
      console.error('🚨 Firebase function call failed:', error);
      throw error;
    }
  },

  async deleteBlockImage(serial, imageType = 'profile') {
    return await apiService.callFunction('blockOperationsCompat', {
      operation: 'deleteBlockImage',
      serial,
      imageType
    });
  },

  async deleteBlock(serial, confirmationSerial) {
    return await apiService.callFunction('blockOperations', {
      operation: 'deleteBlock',
      serial,
      confirmationSerial
    }, { 
      identifier: serial,
      timeout: 60000 // Longer timeout for deletion operations
    });
  },

  async addRefill(refillData) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'addRefill', ...refillData }
    );
  },

  async getDispenseHistory(serial, startDate = null, endDate = null) {
    const params = { serial };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    // Convert params to query string for GET request
    const queryString = new URLSearchParams(params).toString();
    
    // Call the direct Firebase Function endpoint
    const url = `https://getdispensehistory-ziqimm3daq-uc.a.run.app?${queryString}`;
    
    try {
      const headers = await apiService.getAuthHeaders();
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw apiService.handleApiError(error, 'getDispenseHistory', serial);
    }
  },

  async resolveError(serial, errorId) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'resolveError', serial, errorId }, 
      { identifier: serial }
    );
  },

  async resolveWarning(serial, warningId) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'resolveWarning', serial, warningId }, 
      { identifier: serial }
    );
  },

  async resolveAllErrors(serial) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'resolveAllErrors', serial }, 
      { identifier: serial }
    );
  },

  async resolveAllWarnings(serial) {
    return await apiService.callFunction('blockOperations', 
      { operation: 'resolveAllWarnings', serial }, 
      { identifier: serial }
    );
  }
};

/**
 * CONFIGURATION API METHODS
 * These handle system configuration operations
 */
export const configApi = {
  async getSunscreenConfiguration() {
    return await apiService.callRestApi('getSunscreenConfiguration', null, 'GET');
  },

  async updateSunscreenConfiguration(config) {
    return await apiService.callRestApi('updateSunscreenConfiguration', 
      config, 
      'POST'
    );
  }
};

/**
 * WEATHER API METHODS
 * These handle weather webhook processing and integration
 */
export const weatherApi = {
  async processWeatherData(weatherData) {
    return await apiService.callRestApi('weatherData', 
      weatherData, 
      'POST'
    );
  },

  async processUVData(uvData) {
    return await apiService.callRestApi('uvData', 
      uvData, 
      'POST'
    );
  },

  async getWeatherStatus(serial) {
    const params = { serial };
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `getWeatherStatus?${queryString}`;
    
    return await apiService.callRestApi(endpoint, 
      null, 
      'GET', 
      { identifier: serial }
    );
  },

  async updateWeatherConfig(serial, config) {
    return await apiService.callRestApi('updateWeatherConfig', 
      { serial, ...config }, 
      'POST', 
      { identifier: serial }
    );
  }
};

/**
 * USER MANAGEMENT API METHODS
 * These handle user-related operations through the userOperations super-handler
 */
export const userApi = {
  async createUserInvitation(invitationData) {
    // Use super-handler for user invitations
    return await apiService.callFunction('userOperations', {
      operation: 'createUserInvitation',
      ...invitationData
    });
  },

  async updateUserRole(userId, roleData) {
    // Use super-handler for role updates
    return await apiService.callFunction('userOperations', {
      operation: 'updateUserRole',
      userId,
      ...roleData
    });
  },

  async deleteUser(userId, confirmationEmail) {
    // Use super-handler for user deletion
    return await apiService.callFunction('userOperations', {
      operation: 'deleteUser',
      userId,
      confirmationEmail
    });
  },

  async deactivateUser(userId) {
    // Use super-handler for user deactivation
    return await apiService.callFunction('userOperations', {
      operation: 'deactivateUser',
      userId
    });
  },

  async activateUser(userId) {
    // Use super-handler for user activation
    return await apiService.callFunction('userOperations', {
      operation: 'activateUser',
      userId
    });
  },

  async resendInvitation(email) {
    // Use super-handler for invitation resend
    return await apiService.callFunction('userOperations', {
      operation: 'resendInvitation',
      email
    });
  }
};

/**
 * LOADING STATE HELPERS
 * These provide easy access to loading states for UI components
 */
export const loadingUtils = {
  isLoading(operation, identifier = '') {
    const key = apiService.createLoadingKey(operation, identifier);
    return apiService.isLoading(key);
  },

  // Device operation loading states
  isDeviceLoading: (operation, serial) => loadingUtils.isLoading(operation, serial),
  isPaymentConfirming: (serial) => loadingUtils.isDeviceLoading('paymentConfirmed', serial),
  isModeChanging: (serial) => loadingUtils.isDeviceLoading('setMode', serial),
  isResetting: (serial) => loadingUtils.isDeviceLoading('reset', serial),
  isDiagnosticsRunning: (serial) => loadingUtils.isDeviceLoading('diagnostics', serial),
  isMotorControlling: (serial) => loadingUtils.isDeviceLoading('motorControl', serial),
  isClearingErrors: (serial) => loadingUtils.isDeviceLoading('clearErrors', serial),
  isWeatherControlling: (serial) => loadingUtils.isDeviceLoading('weatherControl', serial),
  isUVControlling: (serial) => loadingUtils.isDeviceLoading('uvControl', serial),
  
  // Block management loading states
  isBlockLoading: (operation, serial) => loadingUtils.isLoading(operation, serial),
  isCreatingBlock: (serial) => loadingUtils.isBlockLoading('createBlock', serial),
  isUpdatingLocation: (serial) => loadingUtils.isBlockLoading('updateBlockLocation', serial),
  isUpdatingImage: (serial) => loadingUtils.isBlockLoading('updateBlockImage', serial),
  isLoggingMaintenance: (serial) => loadingUtils.isBlockLoading('logMaintenance', serial),
  
  // General loading states
  isLoadingBlocks: () => loadingUtils.isLoading('getBlocksWithStats'),
  isLoadingConfig: () => loadingUtils.isLoading('getSunscreenConfiguration')
};

/**
 * Export the core API service for advanced usage
 */
export { apiService };

/**
 * Export all API functionality
 */

/**
 * Default export for easy importing
 */
export default {
  device: deviceApi,
  block: blockApi,
  config: configApi,
  weather: weatherApi,
  loading: loadingUtils,
  service: apiService,
  errors: API_ERROR_TYPES,
  // Direct access to commonly used functions
  getBlocksWithStats: () => apiService.getBlocksWithStats(),
  getSunscreenConfiguration: () => configApi.getSunscreenConfiguration(),
  getDispenseHistory: async (serial, startDate, endDate, limit = null) => {
    const requestData = { serial, limit };
    if (startDate) requestData.startDate = startDate;
    if (endDate) requestData.endDate = endDate;
    
    try {
      // Call the direct getDispenseHistory Firebase Function
      const response = await apiService.callFunction('getDispenseHistory', requestData);
      
      // Ensure consistent data structure
      if (response && response.success && response.data) {
        return {
          success: true,
          data: response.data,
          count: response.count || response.data.length || 0
        };
      } else if (response && Array.isArray(response)) {
        // Handle case where response is directly an array
        return {
          success: true,
          data: response,
          count: response.length
        };
      } else {
        // Return empty data structure if no valid response
        return {
          success: true,
          data: [],
          count: 0
        };
      }
    } catch (error) {
      throw apiService.handleApiError(error, 'getDispenseHistory', serial);
    }
  }
};
